import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import * as Sentry from "@sentry/react"
import {
  CreditCard,
  Check,
  Calendar,
  Clock,
  AlertCircle,
  MapPin,
  Navigation,
  Star,
  Shield,
  Timer,
  ChevronDown,
  ChevronUp,
  Lock,
  X
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Header } from '../components/Layout/Header'
import { subscriptionService, TrialStatusResponse } from '../services/subscriptionService'
import AnalyticsService from '../services/analyticsService'
import { SUPPORT_EMAIL } from '../constants/contact'
import emailService from '../services/emailService'
import { useLocalization } from '../contexts/LocalizationContext'
import { useAudit } from '../hooks/useAudit'
import PostbackService from '../services/postbackService'
import localizationService from '../services/localizationService'



// Composant Accordéon pour le mobile
const AccordionItem = ({ title, children, isOpen, onClick }: { title: React.ReactNode, children: React.ReactNode, isOpen: boolean, onClick: () => void }) => {
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        className="w-full py-4 px-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
        onClick={onClick}
      >
        <span className="font-medium text-gray-900 text-left"><span>{title}</span></span>
        {isOpen ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-4 bg-gray-50 text-sm text-gray-600">
          {children}
        </div>
      </div>
    </div>
  )
}

const SubscriptionV6 = () => {
  const { user, subscription, checkSubscription, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const { testAuditLogging, logSystemError } = useAudit()
  const [localLoading, setLocalLoading] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [paymentResult, setPaymentResult] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
    details?: Record<string, string>
  } | null>(null)
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentFormDisplayed, setPaymentFormDisplayed] = useState(false)
  const [trialStatus, setTrialStatus] = useState<TrialStatusResponse | null>(null)
  const [currentTrialSubscriptionId, setCurrentTrialSubscriptionId] = useState<string | null>(null)
  const [currentFormToken, setCurrentFormToken] = useState<string | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [isVerifyingSubscription, setIsVerifyingSubscription] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [showTermsTooltip, setShowTermsTooltip] = useState(false)
  const [preloading, setPreloading] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState<string>('')
  const { t, formatPrice, config } = useLocalization()

  // Accordion states
  const [openAccordion, setOpenAccordion] = useState<string | null>(null)
  // Ref for payment container to avoid direct DOM manipulation
  const paymentContainerRef = useRef<HTMLDivElement>(null)
  // Ref for polling interval
  const pollingRef = useRef<NodeJS.Timeout | null>(null)

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id)
  }

  useEffect(() => {
    if (user?.email && !authLoading) {
      loadSubscriptionInfo()
    }
    loadSystemPayScript()
  }, [user?.email, authLoading])

  useEffect(() => {
    if (!authLoading && subscription && subscription.status === 'active') {
      console.log('👁️ Utilisateur avec abonnement actif - affichage du statut sur la page d\'abonnement')
    }
  }, [authLoading, subscription])

  // Cleanup SystemPay elements when component unmounts or payment form is hidden
  useEffect(() => {
    return () => {
      // Cleanup polling
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
        pollingRef.current = null
      }
      // Cleanup when component unmounts
      if (paymentContainerRef.current) {
        paymentContainerRef.current.innerHTML = ''
      }
    }
  }, [])

  // Pré-chargement du formulaire en arrière-plan
  useEffect(() => {
    const shouldPreload = 
      user?.email && 
      !authLoading && 
      !localLoading && 
      !currentFormToken && 
      !preloading &&
      (!subscription || (subscription.status !== 'active' && subscription.status !== 'trial'))

    if (shouldPreload) {
      // Petit délai pour ne pas bloquer le rendu initial
      const timer = setTimeout(() => {
        preloadSubscription()
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [user?.email, authLoading, localLoading, subscription, currentFormToken, preloading])

  const preloadSubscription = async () => {
    if (!user?.email) return

    try {
      console.log('🚀 Pré-chargement du formulaire d\'abonnement...')
      setPreloading(true)
      
      const fullName = user.user_metadata?.full_name || user.email.split('@')[0]
      const result = await subscriptionService.createTrialSubscriptionV3(user.email, user.id, fullName)

      if (result.success && result.data) {
        console.log('✅ Pré-chargement réussi, token reçu')
        setCurrentOrderId(result.data.orderId)
        setCurrentTrialSubscriptionId(result.data.subscriptionId)
        setCurrentFormToken(result.data.formToken)
        
        // Charger le formulaire dans le conteneur (qui est masqué)
        await displayPaymentForm(result.data.formToken)
      }
    } catch (error) {
      console.error('❌ Erreur lors du pré-chargement:', error)
      // On ne montre pas d'erreur à l'utilisateur ici, on réessaiera au clic
    } finally {
      setPreloading(false)
    }
  }


  const loadSystemPayScript = () => {
    if (!document.querySelector('script[src*="kr-payment-form"]')) {
      // Utiliser la config du hook directement comme en V3 pour éviter l'attente asynchrone
      const systemPayLanguage = config?.language === 'fr' ? 'fr-FR' : 'en-GB'
      console.log('🌍 SystemPay chargé avec la langue:', systemPayLanguage)

      const script = document.createElement('script')
      script.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js'
      script.setAttribute('kr-public-key', '67361502:publickey_YK1k7LNpmzU6usYlVGXEO4WOMAsezDDdgnongY2ErOFhe')
      script.setAttribute('kr-language', systemPayLanguage)
      document.head.appendChild(script)

      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon-reset.min.css'
      document.head.appendChild(link)

      const neonScript = document.createElement('script')
      neonScript.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon.js'
      document.head.appendChild(neonScript)
    }
  }

  const loadSubscriptionInfo = async (forceRefresh: boolean = false) => {
    try {
      setLocalLoading(true)
      await checkSubscription(forceRefresh)
    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'abonnement:', error)
      logSystemError('LOAD_SUBSCRIPTION_INFO', error, {
        userEmail: user?.email,
        subscriptionStatus: subscription?.status
      })
      // Envoyer l'erreur à Sentry pour monitoring complet
      Sentry.captureException(error, {
        tags: {
          component: 'SubscriptionV6',
          function: 'loadSubscriptionInfo'
        },
        extra: {
          userEmail: user?.email,
          subscriptionStatus: subscription?.status
        }
      })
    } finally {
      setLocalLoading(false)
    }
  }

  const displayPaymentForm = async (formToken: string, retryCount = 0) => {
    try {
      // Attendre que SystemPay soit chargé (avec timeout de 10s)
      await new Promise<void>((resolve, reject) => {
        let attempts = 0
        const maxAttempts = 100 // 10 secondes (100 * 100ms)
        
        const checkKR = () => {
          if (window.KR) {
            resolve()
          } else {
            attempts++
            if (attempts >= maxAttempts) {
              reject(new Error('Timeout waiting for SystemPay to load'))
            } else {
              setTimeout(checkKR, 100)
            }
          }
        }
        checkKR()
      })

      if (window.KR) {
        const systemPayLanguage = config?.language === 'fr' ? 'fr-FR' : 'en-GB'
        console.log('🌍 Configuration du formulaire SystemPay avec la langue:', systemPayLanguage)

        await window.KR.setFormConfig({
          formToken: formToken,
          'kr-language': systemPayLanguage
        })

        // Use React ref instead of direct DOM manipulation
        const container = paymentContainerRef.current
        if (container) {
          // Clear existing content first
          container.innerHTML = ''
          // Create the SystemPay form element
          const formElement = document.createElement('div')
          formElement.className = 'kr-smart-form'
          formElement.setAttribute('kr-no-card-logo-header', '')
          container.appendChild(formElement)

          await window.KR.renderElements('#payment-container')
          setPaymentFormDisplayed(true)
          console.log('✅ Formulaire de paiement affiché avec succès')
        }
      }

    } catch (error) {
      console.error(`❌ Erreur lors de l'affichage du formulaire (tentative ${retryCount + 1}/3):`, error)

      if (retryCount < 2) {
        console.log('🔄 Tentative de rechargement du script SystemPay...')
        
        // Nettoyer les scripts existants pour forcer le rechargement
        const scripts = document.querySelectorAll('script[src*="kr-payment-form"]')
        scripts.forEach(s => s.remove())
        const neonScripts = document.querySelectorAll('script[src*="neon.js"]')
        neonScripts.forEach(s => s.remove())
        const neonStyles = document.querySelectorAll('link[href*="neon-reset.min.css"]')
        neonStyles.forEach(s => s.remove())
        
        // Recharger le script
        loadSystemPayScript()
        
        // Réessayer l'affichage après un court délai
        return new Promise(resolve => setTimeout(() => {
          resolve(displayPaymentForm(formToken, retryCount + 1))
        }, 1000))
      }

      // Logger l'erreur avec contexte détaillé
      logSystemError('PAYMENT_FORM_DISPLAY', error, {
        formToken: formToken ? 'present' : 'missing',
        containerExists: !!paymentContainerRef.current,
        paymentFormDisplayed,
        showPaymentForm,
        windowKRExists: !!window.KR,
        krMethods: window.KR ? Object.keys(window.KR) : [],
        retryCount
      })

      // Envoyer l'erreur à Sentry pour monitoring complet
      Sentry.captureException(error, {
        tags: {
          component: 'SubscriptionV6',
          function: 'displayPaymentForm'
        },
        extra: {
          formToken: formToken ? 'present' : 'missing',
          containerExists: !!paymentContainerRef.current,
          paymentFormDisplayed,
          showPaymentForm,
          windowKRExists: !!window.KR,
          retryCount
        }
      })

      setPaymentResult({
        type: 'error',
        title: t('errors.generic'),
        message: t('subscriptionV6.errors.paymentFormLoadFailed')
      })
      setShowPaymentForm(false)
    }
  }

  const handleStartTrialV3 = async () => {
    if (!user?.email) return

    // Empêcher les double-clics
    if (paymentLoading) {
      console.log('⚠️ Paiement déjà en cours')
      return
    }

    // Si le token est déjà là (pré-chargé), on affiche juste
    if (currentFormToken && currentOrderId) {
      console.log('⚡ Utilisation du formulaire pré-chargé')
      setShowPaymentForm(true)
      
      // Si le formulaire n'est pas encore affiché (cas rare où token est là mais display a échoué ou est en cours)
      if (!paymentFormDisplayed) {
         await displayPaymentForm(currentFormToken)
      }
      
      startTrialPaymentPolling(currentOrderId)
      
      // Scroll vers le formulaire
      setTimeout(() => {
        const element = document.getElementById('payment-section')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
      
      return
    }

    try {
      setPaymentLoading(true)
      setPaymentResult(null)
      
      AnalyticsService.trackEvent('begin_checkout', {
        event_category: 'subscription',
        event_label: 'trial_v6_start',
        currency: config?.currency || 'USD',
        value: 0,
      })

      const fullName = user.user_metadata?.full_name || user.email.split('@')[0]

      const result = await subscriptionService.createTrialSubscriptionV3(user.email, user.id, fullName)

      if (result.success && result.data) {
        setCurrentOrderId(result.data.orderId)
        setCurrentTrialSubscriptionId(result.data.subscriptionId)
        setCurrentFormToken(result.data.formToken)
        
        setShowPaymentForm(true)
        
        // Afficher le formulaire directement (comme V3)
        await displayPaymentForm(result.data.formToken)
        
        startTrialPaymentPolling(result.data.orderId)
        
      } else {
        throw new Error(result.message || t('errors.generic'))
      }

    } catch (error) {
      console.error('❌ Error:', error)
      AnalyticsService.trackError('trial_v6_creation_error', error instanceof Error ? error.message : 'Unknown trial V6 creation error')

      // Logger l'erreur de création d'abonnement
      logSystemError('TRIAL_CREATION', error, {
        userEmail: user?.email,
        userId: user?.id,
        termsAccepted,
        paymentLoading,
        currentFormToken: currentFormToken ? 'present' : 'missing'
      })

      // Envoyer l'erreur à Sentry pour monitoring complet
      Sentry.captureException(error, {
        tags: {
          component: 'SubscriptionV6',
          function: 'handleStartTrialV3'
        },
        extra: {
          userEmail: user?.email,
          userId: user?.id,
          termsAccepted,
          paymentLoading,
          currentFormToken: currentFormToken ? 'present' : 'missing'
        }
      })

      setPaymentResult({
        type: 'error',
        title: t('errors.generic'),
        message: error instanceof Error ? error.message : t('errors.generic')
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const startTrialPaymentPolling = (orderId: string) => {
    // Prevent multiple polling intervals
    if (pollingRef.current) {
      console.log('⚠️ Polling déjà en cours, ignoré')
      return
    }

    console.log('🔄 Démarrage du polling pour:', orderId)

    const poll = async () => {
      try {
        // Pour V3 (essai gratuit), on vérifie directement le statut d'abonnement
        // car il n'y a pas de transaction classique
        if (user?.email) {
          const statusResult = await subscriptionService.checkSubscriptionStatus(user.email)
          
          if (statusResult && (statusResult.status === 'active' || statusResult.status === 'trial')) {
            console.log('✅ Abonnement actif détecté !')
            pollingRef.current = null // Stop polling

            const eventId = user?.user_metadata?.event_id
            if (eventId) {
              PostbackService.sendPaymentPostback(eventId, 0)
            }

            AnalyticsService.trackSubscription('trial_v6')
            AnalyticsService.trackEvent('purchase', {
              event_category: 'subscription',
              event_label: 'trial_v6_success',
              currency: config?.currency || 'USD',
              value: 0,
            })

            if (user?.email) {
              emailService.sendTrialWelcomeEmailAsync(user.email, user.email.split('@')[0])
                .catch(error => console.warn('⚠️ Erreur email:', error))
            }
            
            setPaymentResult({
              type: 'success',
              title: t('subscription.trial.activated'),
              message: t('subscription.trial.message')
            })

            setPaymentSuccess(true)
            setShowPaymentForm(false)
            
            // Mettre à jour le contexte global
            await checkSubscription(true)
            
            // Redirection après un court délai
            setTimeout(() => {
              navigate('/dashboard')
            }, 1500)
            return
          }
        }

        // Fallback: vérifier aussi le statut de transaction pour les cas où le paiement est requis
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            pollingRef.current = null // Stop polling

            const eventId = user?.user_metadata?.event_id
            if (eventId) {
              PostbackService.sendPaymentPostback(eventId, 0)
            }

            AnalyticsService.trackSubscription('trial_v6')
            AnalyticsService.trackEvent('purchase', {
              event_category: 'subscription',
              event_label: 'trial_v6_success',
              currency: config?.currency || 'USD',
              value: 0,
            })

            if (user?.email) {
              emailService.sendTrialWelcomeEmailAsync(user.email, user.email.split('@')[0])
                .catch(error => console.warn('⚠️ Erreur email:', error))
            }
            
            setPaymentResult({
              type: 'success',
              title: t('subscription.trial.activated'),
              message: t('subscription.trial.message')
            })

            setPaymentSuccess(true)
            setShowPaymentForm(false)
            setIsVerifyingSubscription(true)
            
            // Polling pour vérifier l'activation de l'abonnement
            let attempts = 0
            const maxAttempts = 15 // 30 secondes max (15 * 2s)
            
            const verifySubscriptionLoop = async () => {
              try {
                console.log(`🔄 Vérification abonnement (tentative ${attempts + 1}/${maxAttempts})...`)
                
                // Vérifier directement via le service pour avoir la réponse brute
                if (user?.email) {
                  const statusResult = await subscriptionService.checkSubscriptionStatus(user.email)
                  
                  if (statusResult && (statusResult.status === 'active' || statusResult.status === 'trial')) {
                    console.log('✅ Abonnement actif détecté !')
                    // Mettre à jour le contexte global
                    await checkSubscription(true)
                    
                    // Petit délai pour l'UX
                    setTimeout(() => {
                      navigate('/dashboard')
                    }, 1000)
                    return
                  }
                }
                
                attempts++
                if (attempts < maxAttempts) {
                  setTimeout(verifySubscriptionLoop, 2000)
                } else {
                  console.error('❌ Timeout vérification abonnement')
                  setIsVerifyingSubscription(false)
                  setPaymentResult({
                    type: 'error',
                    title: t('subscription.verifying.delayedTitle'),
                    message: t('subscription.verifying.delayedMessage')
                  })
                }
              } catch (error) {
                console.error('❌ Erreur vérification:', error)
                attempts++
                if (attempts < maxAttempts) {
                  setTimeout(verifySubscriptionLoop, 2000)
                }
              }
            }
            
            verifySubscriptionLoop()
            return // Stop polling
            
          } else if (status === 'failed') {
            pollingRef.current = null // Stop polling
            AnalyticsService.trackError('payment_failed', 'Trial V6 payment method registration failed')
            
            // Logger l'échec du paiement
            logSystemError('PAYMENT_FAILED', new Error('Payment method registration failed'), {
              orderId,
              subscriptionId: currentTrialSubscriptionId,
              transactionStatus: status,
              userEmail: user?.email
            })
            
            setShowPaymentForm(false)
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationFailedTitle'),
              message: t('subscriptionV3.paymentRegistrationFailedMessage')
            })
            return // Stop polling
            
          } else if (status === 'cancelled') {
            pollingRef.current = null // Stop polling
            AnalyticsService.trackEvent('payment_cancelled', {
              event_category: 'subscription',
              event_label: 'trial_v6_payment_cancelled',
            })
            
            // Logger l'annulation du paiement
            logSystemError('PAYMENT_CANCELLED', new Error('Payment cancelled by user'), {
              orderId,
              subscriptionId: currentTrialSubscriptionId,
              transactionStatus: status,
              userEmail: user?.email
            })
            
            setShowPaymentForm(false)
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationCancelledTitle'),
              message: t('subscriptionV3.paymentRegistrationCancelledMessage')
            })
            return // Stop polling
          }
        }
      } catch (error) {
        console.error('❌ Error checking payment status:', error)
        logSystemError('PAYMENT_STATUS_CHECK', error, {
          orderId,
          subscriptionId: currentTrialSubscriptionId,
          userEmail: user?.email
        })
        // Envoyer l'erreur à Sentry pour monitoring complet
        Sentry.captureException(error, {
          tags: {
            component: 'SubscriptionV6',
            function: 'startTrialPaymentPolling'
          },
          extra: {
            orderId,
            subscriptionId: currentTrialSubscriptionId,
            userEmail: user?.email
          }
        })
      }

      // Schedule next poll if not stopped
      if (pollingRef.current) {
        pollingRef.current = setTimeout(poll, 1000)
      }
    }

    // Start first poll
    pollingRef.current = setTimeout(poll, 1000)

    // Global timeout
    setTimeout(() => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
        pollingRef.current = null
      }
    }, 300000)
  }

  const handleCancelSubscription = () => {
    setShowCancelModal(true)
  }

  const confirmCancellation = async () => {
    if (!subscription || !user?.email) return

    const subscriptionType = subscription.status === 'trial' ? 'trial' : 'subscription'
    
    try {
      setCancelLoading(true)
      const result = await subscriptionService.cancelSubscription(user.email, true, cancelReason || t('subscription.cancelModal.reasons.unspecified'))
      
      if (result.success) {
        AnalyticsService.trackEvent('subscription_cancelled', {
          event_category: 'subscription',
          event_label: subscription.status === 'trial' ? 'trial_v6_cancelled' : 'subscription_v6_cancelled',
          reason: cancelReason
        })
        
        if (user?.email) {
          emailService.sendCancellationEmailAsync(
            user.email, 
            subscriptionType as 'trial' | 'subscription',
            user.email.split('@')[0],
            subscription.expiryDate
          ).catch(error => console.warn('⚠️ Erreur email:', error))
        }
        
        const successMessage = subscription.status === 'trial' 
          ? t('subscriptionV3.cancelSuccessTrial')
          : t('subscriptionV3.cancelSuccessSubscription')
        
        alert(successMessage)
        setShowCancelModal(false)
        await checkSubscription(true)
        setTimeout(() => {
          navigate('/')
        }, 2000)
        
      } else {
        throw new Error(result.message || t('errors.generic'))
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('errors.generic')

      // Logger l'erreur d'annulation
      logSystemError('SUBSCRIPTION_CANCELLATION', error, {
        subscriptionStatus: subscription?.status,
        subscriptionType,
        userEmail: user?.email,
        reason: cancelReason
      })

      // Envoyer l'erreur à Sentry pour monitoring complet
      Sentry.captureException(error, {
        tags: {
          component: 'SubscriptionV6',
          function: 'handleCancelSubscription'
        },
        extra: {
          subscriptionStatus: subscription?.status,
          subscriptionType,
          userEmail: user?.email,
          reason: cancelReason
        }
      })

      alert(`${t('errors.generic')}: ${errorMessage}`)
      if (errorMessage.includes('Aucun abonnement trouvé') || errorMessage.includes('No subscription found')) {
        await loadSubscriptionInfo()
      }
    } finally {
      setCancelLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header />
      
      {/* Cancellation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-gray-900">{t('subscription.cancelModal.title')}</h3>
                <button 
                  onClick={() => setShowCancelModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-xl p-5 mb-6">
                <p className="font-semibold text-red-800 mb-3">{t('subscription.cancelModal.subtitle')}</p>
                <ul className="space-y-3">
                  <li className="flex items-start text-red-700">
                    <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{t('subscription.cancelModal.losses.realTimeMap')}</span>
                  </li>
                  <li className="flex items-start text-red-700">
                    <Navigation className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{t('subscription.cancelModal.losses.gpsNavigation')}</span>
                  </li>
                  <li className="flex items-start text-red-700">
                    <Star className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{t('subscription.cancelModal.losses.favorites')}</span>
                  </li>
                  <li className="flex items-start text-red-700 font-bold">
                    <Shield className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{t('subscription.cancelModal.losses.savedParkings')}</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('subscription.cancelModal.reasonLabel')}
                </label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                >
                  <option value="">{t('subscription.cancelModal.reasons.placeholder')}</option>
                  <option value="too_expensive">{t('subscription.cancelModal.reasons.tooExpensive')}</option>
                  <option value="not_using_enough">{t('subscription.cancelModal.reasons.notUsingEnough')}</option>
                  <option value="found_alternative">{t('subscription.cancelModal.reasons.foundAlternative')}</option>
                  <option value="technical_issues">{t('subscription.cancelModal.reasons.technicalIssues')}</option>
                  <option value="other">{t('subscription.cancelModal.reasons.other')}</option>
                </select>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="w-full py-4 px-6 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-green-200 transition-all transform hover:scale-[1.02]"
                >
                  {t('subscription.cancelModal.keepSubscription')}
                </button>
                
                <button
                  onClick={confirmCancellation}
                  disabled={cancelLoading}
                  className="w-full py-3 px-6 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl font-medium text-sm transition-colors"
                >
                  {cancelLoading ? t('subscription.cancelModal.cancelling') : t('subscription.cancelModal.confirmCancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Responsive Container - Full width on desktop, centered on mobile */}
      <div className="w-full lg:max-w-7xl lg:mx-auto px-4 pt-0">
        
        {authLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}

        {paymentSuccess && !isVerifyingSubscription && (
          <div className="bg-white shadow-lg rounded-xl p-8 text-center animate-fade-in">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('subscription.trial.activated')}</h3>
            <p className="text-gray-600">{t('subscriptionV3.redirectingToDashboard')}</p>
          </div>
        )}

        {isVerifyingSubscription && (
          <div className="bg-white shadow-lg rounded-xl p-8 text-center animate-fade-in">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('subscription.verifying.title')}</h3>
            <p className="text-gray-600">{t('subscription.verifying.message')}</p>
            <p className="text-sm text-gray-400 mt-4">{t('subscription.verifying.hint')}</p>
          </div>
        )}

        {/* Active Trial State */}
        {!authLoading && subscription && subscription.status === 'trial' && !paymentSuccess && (
          <div className="bg-white shadow-lg rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white text-center">
              <Timer className="w-12 h-12 mx-auto mb-3 opacity-90" />
              <h4 className="text-2xl font-bold mb-1">{t('subscription.trial.activeTitle')}</h4>
              <p className="opacity-90">{t('subscriptionV3.remainingDays', { count: subscription.daysUntilNext })}</p>
            </div>
            
            <div className="p-6">
              <div className="mb-6 text-center">
                <p className="text-gray-700 mb-4">
                  {t('subscription.trial.activeDescription')}
                </p>
                <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                  {t('subscription.trial.noChargeBeforeEnd')}
                </p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center text-gray-700">
                  <div className="bg-green-100 rounded-full p-1 mr-3 flex-shrink-0">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <span>{t('subscription.trial.features.realTimeMap')}</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="bg-green-100 rounded-full p-1 mr-3 flex-shrink-0">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <span>{t('subscription.trial.features.gpsNavigation')}</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="bg-green-100 rounded-full p-1 mr-3 flex-shrink-0">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <span>{t('subscription.trial.features.favorites')}</span>
                </div>
              </div>

              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-4 px-6 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-green-200 mb-6 transition-all transform hover:scale-[1.02] flex items-center justify-center"
              >
                <span>{t('subscription.trial.goToDashboard')}</span>
                <ChevronDown className="ml-2 h-5 w-5 rotate-[-90deg]" />
              </button>

              <div className="border-t border-gray-100 pt-6">
                <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 font-medium">
                      {t('subscription.trial.cancelWarning')}
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                  className="w-full py-2 px-4 text-gray-400 hover:text-red-600 text-sm font-medium transition-colors hover:underline"
                >
                  {cancelLoading ? <span>{t('subscriptionV3.cancelling')}</span> : <span>{t('subscriptionV3.cancelTrial')}</span>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No Subscription / Sales Page */}
        {!authLoading && (!subscription || (subscription.status !== 'active' && subscription.status !== 'trial')) && (
          <div className="space-y-6">
            {/* Desktop Grid Layout - Full width on desktop */}
            <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-start">

              {/* Left Column - Main Content */}
              <div className="space-y-6">
                {/* Hero / Value Prop */}
                <div className={`text-center space-y-4 lg:text-left ${showPaymentForm ? 'hidden' : ''}`}>
                    <h1 className="text-3xl font-extrabold text-green-600 leading-tight">
                      {t('subscriptionV6.heroTitle')}
                    </h1>
                    <p className="text-lg text-gray-600">
                      {t('subscriptionV6.heroSubtitle')}
                    </p>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-6">
                      <ul className="space-y-3 text-left mb-8">
                        <li className="flex items-start">
                          <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                            <Check className="h-3 w-3 text-green-600" />
                          </div>
                          <span className="text-gray-700">{t('subscriptionV6.features.realTimeMap')}</span>
                        </li>
                        <li className="flex items-start">
                          <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                            <Check className="h-3 w-3 text-green-600" />
                          </div>
                          <span className="text-gray-700">{t('subscriptionV6.features.gpsNavigation')}</span>
                        </li>
                        <li className="flex items-start">
                          <div className="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                            <Check className="h-3 w-3 text-green-600" />
                          </div>
                          <span className="text-gray-700">{t('subscriptionV6.features.easyCancellation')}</span>
                        </li>
                      </ul>

                      <div className="flex items-start mb-4 text-left relative">
                        <div className="flex items-center h-5">
                          <input
                            id="terms-checkbox"
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={(e) => {
                              setTermsAccepted(e.target.checked)
                              if (e.target.checked) {
                                setShowTermsTooltip(false)
                              }
                            }}
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                          />
                        </div>
                        <label htmlFor="terms-checkbox" className="ml-2 block text-sm text-gray-600 cursor-pointer">
                          {t('subscriptionV6.termsAcceptance')} <Link to="/terms" className="text-green-600 hover:underline font-medium" target="_blank">{t('subscriptionV6.termsLink')}</Link>
                        </label>

                        {/* Tooltip */}
                        {showTermsTooltip && (
                          <div className="absolute top-8 left-0 right-0 z-10 bg-red-50 border border-red-200 rounded-lg p-3 shadow-lg animate-fade-in">
                            <div className="flex items-start">
                              <div className="bg-red-100 rounded-full p-1 mr-2 mt-0.5 flex-shrink-0">
                                <AlertCircle className="h-3 w-3 text-red-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-red-900">{t('subscriptionV6.tooltip.title')}</p>
                                <p className="text-xs text-red-700 mt-1">
                                  {t('subscriptionV6.tooltip.message')}
                                </p>
                              </div>
                            </div>
                            <div className="absolute -top-2 left-6 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-red-50"></div>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          if (!termsAccepted) {
                            setShowTermsTooltip(true)
                            return
                          }
                          handleStartTrialV3()
                        }}
                        disabled={paymentLoading}
                        className={`w-full py-4 px-6 rounded-xl text-lg font-bold shadow-lg flex items-center justify-center transition-all ${
                          paymentLoading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                            : 'bg-green-600 text-white shadow-green-200 hover:bg-green-700 transform active:scale-95'
                        }`}
                      >
                        {paymentLoading ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <span>{t('subscriptionV6.activateAccount')}</span>
                            <ChevronDown className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-400 mt-3 flex items-center justify-center">
                        <Lock className="h-3 w-3 mr-1" /> {t('subscriptionV6.paymentDisclaimer')}
                      </p>
                    </div>
                  </div>

                {/* Payment Form Section */}
                <div id="payment-section" className={`bg-white rounded-xl shadow-lg border border-blue-100 ${!showPaymentForm ? 'hidden' : ''}`}>
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                      <div>
                        <h3 className="font-bold text-gray-900">{t('subscriptionV6.securePayment')}</h3>
                        <p className="text-xs text-gray-500">{t('subscriptionV6.sslEncryption')}</p>
                      </div>
                      <div className="flex space-x-2">
                        <img src="https://cdn-icons-png.flaticon.com/512/196/196578.png" alt="Visa" className="h-6" />
                        <img src="https://cdn-icons-png.flaticon.com/512/196/196561.png" alt="Mastercard" className="h-6" />
                      </div>
                    </div>

                    <div className="p-6 pt-1">
                      <div className="mb-2 text-center">
                        <p className="text-gray-700 text-sm font-bold">
                          {t('subscriptionV3.paymentMethodRegistration')}
                        </p>
                      </div>

                      <div className="mb-6 bg-blue-50 rounded-lg p-4 flex items-start">
                        <div className="bg-blue-100 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                          <Clock className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-blue-900">{t('subscriptionV6.totalAmount')}</p>
                        </div>
                      </div>

                      {paymentLoading && (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
                          <p className="text-gray-600"><span>{t('subscriptionV6.loadingPaymentForm')}</span></p>
                        </div>
                      )}

                      <div ref={paymentContainerRef} id="payment-container" className="min-h-[300px]" />

                      <div className="mt-4 flex items-center justify-center text-xs text-gray-400">
                        <Lock className="h-3 w-3 mr-1" />
                        {t('subscriptionV6.secureTransactions')}
                      </div>

                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div>
                          <p className="text-sm text-green-800 font-medium">{t('subscriptionV3.noChargeInfo')}</p>
                          <p className="text-sm text-green-700 mt-2">
                            {t('subscriptionV3.paymentMethodDescription')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                {/* Error Messages */}
                {paymentResult && paymentResult.type === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start animate-shake">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-bold text-red-900">{paymentResult.title}</h4>
                      <p className="text-sm text-red-700 mt-1">{paymentResult.message}</p>
                    </div>
                  </div>
                )}

                {/* Benefits Section (from V3) */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    {t('subscriptionV3.whyChooseTitle')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    
                    {/* Time & Money Savings */}
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <Clock className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {t('subscriptionV3.benefits.saveTime.title')}
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {t('subscriptionV3.benefits.saveTime.description')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <MapPin className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {t('landing.features.smartMap.title')}
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {t('subscriptionV3.benefits.smartMap.description')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <Navigation className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {t('landing.features.navigation.title')}
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {t('subscriptionV3.benefits.navigation.description')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Comfort & Peace of Mind */}
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <Star className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {t('landing.features.favorites.title')}
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {t('subscriptionV3.benefits.favorites.description')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <AlertCircle className="h-6 w-6 text-red-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {t('subscriptionV3.benefits.notifications.title')}
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {t('subscriptionV3.benefits.notifications.description')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                          <Shield className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {t('subscriptionV3.benefits.support.title')}
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {t('subscriptionV3.benefits.support.description')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FAQ Accordion */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <AccordionItem
                    title={t('subscriptionV6.faq.title')}
                    isOpen={openAccordion === 'faq'}
                    onClick={() => toggleAccordion('faq')}
                  >
                    <div className="space-y-4">
                      <div>
                        <p className="font-medium text-gray-900">{t('subscriptionV6.faq.billingQuestion')}</p>
                        <p>{t('subscriptionV6.faq.billingAnswer', { price: formatPrice() })}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{t('subscriptionV6.faq.cancelQuestion')}</p>
                        <p>{t('subscriptionV6.faq.cancelAnswer')}</p>
                      </div>
                    </div>
                  </AccordionItem>
                </div>
              </div>

              {/* Right Column - Statistics */}
              <div className="space-y-6">
                {/* Statistics & CTA Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    {t('subscriptionV6.stats.title')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-center mb-6">
                    <div>
                      <div className="text-2xl font-bold text-green-600 mb-1">15-20 min</div>
                      <div className="text-xs text-gray-600">{t('subscriptionV6.stats.timeSaved')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600 mb-1">98%</div>
                      <div className="text-xs text-gray-600">{t('subscriptionV6.stats.satisfaction')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600 mb-1">200€+</div>
                      <div className="text-xs text-gray-600">{t('subscriptionV6.stats.savings')}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600 mb-1">{formatPrice()}</div>
                      <div className="text-xs text-gray-600">{t('subscriptionV6.stats.price')}</div>
                    </div>
                  </div>

                </div>

                {/* Footer Trust */}
                <div className="text-center py-6 opacity-60 grayscale">
                  <div className="flex justify-center space-x-4 mb-2">
                    <img src="https://cdn-icons-png.flaticon.com/512/196/196578.png" alt="Visa" className="h-6" />
                    <img src="https://cdn-icons-png.flaticon.com/512/196/196561.png" alt="Mastercard" className="h-6" />
                    <img src="https://cdn-icons-png.flaticon.com/128/16009/16009219.png" alt="CB" className="h-6" />
                  </div>
                  <p className="text-xs text-gray-500">{t('subscription.footer.securePayment')}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Subscription (Not Trial) */}
        {!authLoading && subscription && subscription.status === 'active' && (
          <div className="bg-white shadow-lg rounded-xl p-6">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 rounded-full p-2 mr-3">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{t('subscription.activeSubscription.title')}</h3>
                <p className="text-sm text-gray-500">{t('subscription.activeSubscription.premium')}</p>
              </div>
            </div>
            
            <div className="border-t border-gray-100 pt-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('subscription.activeSubscription.nextPayment')}</span>
                <span className="font-medium">{subscription.nextBilling ? new Date(subscription.nextBilling).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>

            <button
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
              className="w-full mt-6 py-2 px-4 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              {cancelLoading ? <span>{t('subscriptionV3.cancelling')}</span> : <span>{t('subscriptionV3.cancelSubscription')}</span>}
            </button>
          </div>
        )}

      </div>
    </div>
  )
}

export default SubscriptionV6
